import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import {
  services,
  testimonials,
  heroImage,
  aboutImage,
  portraitImage,
  offersImage,
  editorialPhotos,
} from "../data/siteData";

const HERO_VIDEOS = [
  { mp4: "/media/hero.mp4", webm: "/media/hero.webm" },
  { mp4: "/media/hero2.mp4", webm: "/media/hero2.webm" },
];

const Home = () => {
  const featured = services.slice(0, 6);
  // Randomly pick one hero video on each mount (page load / refresh).
  // useState's initializer runs exactly once, so the choice is stable for this render lifecycle
  // but re-randomizes on every full page refresh / re-mount.
  const [heroVideo] = useState(
    () => HERO_VIDEOS[Math.floor(Math.random() * HERO_VIDEOS.length)]
  );

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section
        data-testid="hero-section"
        className="relative bg-[#0a0a0a] overflow-hidden"
      >
        {/* EYEBROW above the video — minimal banner */}
        <div className="pt-[80px] lg:pt-[82px] pb-0.5 text-center px-6 lg:px-12 bg-[#0a0a0a]">
          <div className="flex items-center justify-center gap-3 text-[var(--accent)] reveal">
            <span className="welcome-glow text-[10px] lg:text-xs tracking-[0.36em] uppercase font-medium text-white/90">
              Welcome to Society
            </span>
          </div>
        </div>

        {/* VIDEO — clean, no text overlay */}
        <div className="relative w-full bg-black overflow-hidden">
          <video
            key={heroVideo.mp4}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="block w-full mx-auto object-cover h-[44vh] sm:h-[60vh] lg:h-auto lg:max-h-[calc(100vh-140px)] lg:w-auto lg:object-contain [object-position:center_25%] lg:[object-position:center]"
            data-testid="hero-video"
          >
            <source src={heroVideo.webm} type="video/webm" />
            <source src={heroVideo.mp4} type="video/mp4" />
          </video>

          {/* Bottom fade — seamless transition into text band below */}
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent pointer-events-none" />
        </div>

        {/* HEADLINE + CTAs — continuous dark band immediately below the video */}
        <div className="relative bg-[#0a0a0a] pt-3 pb-5 lg:pt-4 lg:pb-7 text-center px-6 lg:px-12 -mt-px">
          <h1
            className="font-display text-white text-[clamp(28px,4.5vw,64px)] leading-[0.96] reveal"
            data-testid="hero-headline"
          >
            Beauty
            <span className="font-elegant italic font-light text-white/95"> isn't a standard.</span>
            <span className="block text-[var(--accent)] mt-1">It's a revolution.</span>
          </h1>

          <div className="flex flex-wrap items-center gap-3 mt-3 lg:mt-4 justify-center reveal delay-1">
            <Link to="/bookings" className="btn-accent" data-testid="hero-book-now">
              Book Now <ArrowRight size={14} />
            </Link>
            <Link to="/portfolio" className="btn-ghost" data-testid="hero-explore">
              Explore
            </Link>
          </div>
        </div>

        {/* Bottom marquee */}
        <div className="hidden lg:block relative border-t border-white/5 bg-[#0a0a0a] py-3 overflow-hidden">
          <div className="marquee text-white/45 text-xs tracking-[0.32em] uppercase">
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex items-center gap-10 pr-10 flex-shrink-0">
                <span>Skin</span><span>·</span>
                <span>Lashes</span><span>·</span>
                <span>Brows</span><span>·</span>
                <span>Permanent Makeup</span><span>·</span>
                <span>Body</span><span>·</span>
                <span>Massage</span><span>·</span>
                <span>Spray Tan</span><span>·</span>
                <span>Ambridge, PA</span><span>·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ABOUT ============ */}
      <section data-testid="about-section" className="bg-[#0a0a0a] py-6 lg:py-10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="text-[var(--accent)] text-xs tracking-[0.32em] uppercase mb-6">Who We Are</p>
            <h2 className="font-display text-white text-[clamp(48px,7vw,120px)] leading-[0.92]">
              Rituals
              <br />
              for the
              <span className="font-elegant italic font-light"> Rebellious.</span>
            </h2>
            <div className="mt-6 lg:mt-10 grid md:grid-cols-2 gap-6 lg:gap-8 text-white/75 text-[15px] leading-relaxed">
              <p>
                Welcome to Society — Ambridge's destination for edgy luxury and
                elevated self-care. We're redefining the day spa experience with a
                bold, vibey atmosphere where relaxation meets rebellion. At Society,
                beauty is a ritual, and every service is designed to leave you
                feeling powerful, radiant, and unapologetically you.
              </p>
              <p>
                Society isn't your typical day spa — we're a movement. Born in
                Ambridge, built for those who crave bold beauty and unapologetic
                luxury. Our space is raw, vibey, and fearless, where self-care
                becomes a statement and every treatment is a ritual of rebellion.
              </p>
            </div>
            <Link to="/about" className="btn-ghost mt-6 lg:mt-10" data-testid="about-explore-more">
              Explore More <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="tile aspect-[4/5]">
              <img src={editorialPhotos[2]} alt="Society — editorial portrait" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ BOOKINGS PREVIEW ============ */}
      <section data-testid="bookings-preview" className="bg-[#0e0e0f] py-6 lg:py-10 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 lg:mb-16">
            <div>
              <p className="text-[var(--accent)] text-xs tracking-[0.32em] uppercase mb-5">Bookings</p>
              <h2 className="font-display text-white text-[clamp(44px,6vw,96px)] leading-[1.02]">
                Signature
                <br /> Services
              </h2>
            </div>
            <p className="text-white/65 max-w-md text-[15px] leading-relaxed">
              From statement brows to skin-transforming facials — explore Society's
              full menu of rituals built to ignite confidence.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 lg:gap-6">
            {featured.map((s, i) => (
              <div
                key={s.id}
                className={`svc-card flex-col p-4 sm:p-6 lg:p-7 ${i >= 4 ? "hidden lg:flex" : "flex"}`}
                data-testid={`service-card-${s.id}`}
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="aspect-[4/3] overflow-hidden rounded-md mb-3 lg:mb-5 tile">
                  <img src={s.img} alt={s.name} loading="lazy" />
                </div>
                <p className="text-[8px] sm:text-[10px] tracking-[0.22em] sm:tracking-[0.28em] uppercase text-[var(--accent)] mb-1 sm:mb-2">{s.category}</p>
                <h3 className="font-display text-white text-lg sm:text-2xl lg:text-3xl mb-1 sm:mb-2 leading-tight">{s.name}</h3>
                <div className="thin-line my-2 lg:my-3 opacity-50" />
                <p className="hidden sm:block text-white/65 text-sm leading-relaxed mb-5">{s.desc}</p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mt-auto">
                  <div className="text-white/85">
                    <span className="font-display text-lg sm:text-2xl">${s.price}</span>
                    <span className="text-white/45 text-[10px] sm:text-xs ml-2">{s.duration}</span>
                  </div>
                  <Link to="/bookings" className="btn-accent text-xs sm:text-sm !px-3 sm:!px-6 !py-2 sm:!py-3 self-start sm:self-auto" data-testid={`book-${s.id}`}>
                    Book Now
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-8 lg:mt-14">
            <Link to="/bookings" className="btn-ghost" data-testid="load-more-services">
              View All Services <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ ICONS ============ */}
      <section data-testid="icons-section" className="bg-[#0a0a0a] py-6 lg:py-10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <h2 className="font-display text-white text-[clamp(36px,5vw,90px)] leading-[0.92] max-w-5xl">
            Where Icons
            <br />
            are <span className="font-elegant italic font-light">made,</span> not born.
          </h2>
          <div className="thin-line mt-6 lg:mt-10 opacity-40 max-w-md" />

          <div className="grid grid-cols-4 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mt-6 lg:mt-10">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <div key={n} className="tile aspect-[3/4]">
                <img src={`/portfolio/icon-${n}.jpg`} alt={`Society editorial ${n}`} loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section data-testid="testimonials-section" className="bg-[#0e0e0f] py-6 lg:py-10 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-10 lg:mb-20">
            <p className="text-[var(--accent)] text-xs tracking-[0.32em] uppercase mb-5">Client Experiences</p>
            <h2 className="font-display text-white text-[clamp(40px,6vw,90px)] leading-[0.95]">
              Come undone.
              <br />
              <span className="font-elegant italic font-light">Leave transformed.</span>
            </h2>
            <p className="text-white/65 mt-6 leading-relaxed">
              Our clients come to Society for more than beauty — they come for transformation.
              Hear from the icons we've helped ignite.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <figure
                key={t.name}
                className="svc-card flex flex-col"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1 text-[var(--accent)] mb-5">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star key={k} size={14} fill="currentColor" stroke="none" />
                  ))}
                </div>
                <blockquote className="font-elegant italic text-white/90 text-2xl leading-snug">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-7 pt-5 border-t border-white/10">
                  <p className="text-white font-display text-xl">{t.name}</p>
                  <p className="text-white/55 text-xs tracking-[0.2em] uppercase mt-1">{t.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ============ OFFERS ============ */}
      <section data-testid="offers-section" className="bg-[#0a0a0a] py-6 lg:py-10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="tile aspect-[3/4]">
              <img src={offersImage} alt="Limited offers" loading="lazy" />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="text-[var(--accent)] text-xs tracking-[0.32em] uppercase mb-5">Special Offers</p>
            <h2 className="font-display text-white text-[clamp(44px,7vw,110px)] leading-[0.92]">
              Limited
              <br />
              <span className="font-elegant italic font-light">time</span> deals.
            </h2>
            <p className="text-white/65 max-w-lg mt-5 lg:mt-8 leading-relaxed">
              Don't miss out on our exclusive promotions. Experience luxury spa
              treatments at discounted rates — for a limited time only.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mt-6 lg:mt-10 max-w-xl">
              <div className="border border-white/10 rounded-xl p-6 hover:border-[var(--accent)]/50 transition-colors">
                <p className="text-[var(--accent)] text-xs tracking-[0.2em] uppercase mb-2">First Visit</p>
                <p className="font-display text-3xl">15% Off</p>
                <p className="text-white/55 text-sm mt-2">Any signature ritual</p>
              </div>
              <div className="border border-white/10 rounded-xl p-6 hover:border-[var(--accent)]/50 transition-colors">
                <p className="text-[var(--accent)] text-xs tracking-[0.2em] uppercase mb-2">Bundle</p>
                <p className="font-display text-3xl">$199</p>
                <p className="text-white/55 text-sm mt-2">Facial + Massage combo</p>
              </div>
            </div>

            <Link to="/bookings" className="btn-accent mt-6 lg:mt-10" data-testid="offers-discover">
              Discover Offers <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ MISSION GRID ============ */}
      <section data-testid="mission-section" className="bg-[#0e0e0f] py-6 lg:py-10 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-10 lg:mb-20 max-w-3xl">
            <p className="text-[var(--accent)] text-xs tracking-[0.32em] uppercase mb-5">Our Commitment</p>
            <h2 className="font-display text-white text-[clamp(36px,5vw,90px)] leading-[0.92]">
              Luxury
              <br />
              <span className="font-elegant italic font-light">without</span> apology.
            </h2>
            <p className="text-white/65 mt-5 lg:mt-8 leading-relaxed">
              At Society, we exist to break the mold of traditional beauty. Our mission
              is to empower every guest to embrace their boldness, elevate their self-care
              rituals, and experience luxury without limits.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {[
              { t: "Quality Services", d: "Top-notch treatments that cater to a diverse range of needs, delivering memorable spa visits." },
              { t: "Client-Focused", d: "Each experience is tailored to your individual expectations and preferences." },
              { t: "Empowering", d: "A space where you feel cared for, valued, and ready to rise — every visit." },
              { t: "Personalized Care", d: "Rituals customized to your exact needs, from your first hello to your last sip of tea." },
            ].map((c, i) => (
              <div
                key={c.t}
                className="bg-[#0e0e0f] p-10 hover:bg-[#141416] transition-colors duration-500"
                data-testid={`mission-card-${i}`}
              >
                <p className="font-display text-[var(--accent)] text-5xl mb-6">0{i + 1}</p>
                <h3 className="font-display text-white text-2xl mb-4">{c.t}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA — Cinematic Editorial ============ */}
      <section data-testid="cta-section" className="relative bg-[#0a0a0a] py-6 lg:py-10 overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="relative rounded-[20px] overflow-hidden border border-white/5">
            {/* Background image */}
            <img
              src="/portfolio/cta-model.jpg"
              alt="Society — bold beauty"
              className="absolute inset-0 w-full h-full object-cover scale-105"
              loading="lazy"
            />
            {/* Layered overlays for cinematic depth */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/65 to-[#0a0a0a]/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/40" />
            <div className="absolute inset-0"
                 style={{ background: "radial-gradient(60% 80% at 80% 50%, rgba(243,168,92,0.18), transparent 70%)" }} />

            {/* Giant ghost gold logo watermark — behind text */}
            <img
              src="/brand/society-gold.png"
              alt=""
              aria-hidden
              className="pointer-events-none absolute -right-12 lg:right-8 top-1/2 -translate-y-1/2 w-[420px] lg:w-[560px] opacity-15 lg:opacity-25 select-none"
              style={{ filter: "drop-shadow(0 0 80px rgba(243,168,92,0.35))" }}
            />

            {/* Inner content grid */}
            <div className="relative grid lg:grid-cols-12 gap-10 px-8 sm:px-12 lg:px-16 py-12 lg:py-32 min-h-[380px] lg:min-h-[680px] items-center">
              {/* Left text block */}
              <div className="lg:col-span-7 max-w-2xl">
                <div className="flex items-center gap-3 mb-7">
                  <span className="w-10 h-px bg-[var(--warm-gold)]" />
                  <span className="text-[10px] tracking-[0.36em] uppercase text-[var(--warm-gold)] font-medium">
                    Your Invitation
                  </span>
                </div>

                <h2 className="font-display text-white text-[clamp(56px,9vw,150px)] leading-[0.86]">
                  Ignite the
                  <span className="block font-elegant italic font-light text-white/95">
                    ritual.
                  </span>
                </h2>

                <p className="text-white/75 text-base lg:text-lg mt-6 lg:mt-10 max-w-lg leading-relaxed">
                  Walk in. Glow different. Your next chapter of bold beauty starts here —
                  one ritual at a time.
                </p>

                <div className="flex flex-wrap items-center gap-4 mt-8 lg:mt-12">
                  <Link to="/bookings" className="btn-accent" data-testid="cta-book-now">
                    Book Your Ritual <ArrowRight size={16} />
                  </Link>
                  <Link to="/contact" className="btn-ghost" data-testid="cta-contact">
                    Get in Touch
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
